import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  HiArrowLeft,
  HiDocumentDuplicate,
  HiInformationCircle,
} from "react-icons/hi2";
import ContentWrapper from "../../components/common/AssessmentContentWrapper";
import Spinner2 from "../../components/Spinner2";
import { routes } from "../../helper/routes";
import {
  ExamGradingProvider,
  useExamGradingContext,
} from "../../context/Exam/ExamGradingContext";
import "./AssessShared.css";
import "./ExamSection.css";
import "./SelfGrading.css";
import { EditorState, convertFromRaw } from "draft-js";
import ViewEditor from "../../components/common/ViewEditor";
import { AzureStorage } from "../../helper/azureStorage";
import config from "../../helper/config";
import StudentPaperHeader from "./StudentPaperHeader";
import "./ResultSheet.css";
import { getSubmissionAttachments } from "./helpers/submissionAnswers";

function getSelfGradingRows(structure = []) {
  return structure.flatMap((part) =>
    part.sections.flatMap((section) =>
      section.questionGroups.flatMap((group) => group.children)
    )
  );
}

function renderModelAnswer(row, onFileDownload) {
  if (row.questionType === "Submission") {
    const attachmentNames =
      row.modelAnswerFiles?.length > 0
        ? row.modelAnswerFiles
        : getSubmissionAttachments(row.modelAnswerRaw);
    return (
      <div className="self-grading-reference-copy">
        {attachmentNames.length > 0 ? (
          <div className="d-flex flex-wrap" style={{ gap: 8 }}>
            {attachmentNames.map((fileName) => (
              <button
                type="button"
                key={fileName}
                className="exam-paper-chip"
                onClick={() =>
                  onFileDownload(row.question_id, fileName, true)
                }
              >
                {fileName}
              </button>
            ))}
          </div>
        ) : null}
        {row.modelAnswerText ? <p>{row.modelAnswerText}</p> : null}
        {!row.modelAnswerText && attachmentNames.length === 0 ? "No model answer available." : null}
      </div>
    );
  }
  return renderDraftContent(row.modelAnswerRaw) || "No model answer available.";
}


function renderSingleDraftRaw(raw, className = "") {
  if (!raw) return null;

  try {
    return (
      <div className={className}>
        <ViewEditor editorState={toEditorState(raw)} />
      </div>
    );
  } catch {
    return null;
  }
}

function safeParseDraftList(content) {
  try {
    const parsed = JSON.parse(content || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function toEditorState(raw) {
  try {
    if (!raw) return EditorState.createEmpty();
    return EditorState.createWithContent(convertFromRaw(raw));
  } catch {
    return EditorState.createEmpty();
  }
}

function renderDraftContent(content, className = "") {
  const parts = safeParseDraftList(content);

  if (!parts.length) return null;

  return (
    <div className={className}>
      {parts.map((raw, index) => (
        <ViewEditor
          key={index}
          editorState={toEditorState(raw)}
        />
      ))}
    </div>
  );
}

function groupByPassage(questionGroups = []) {
  const groups = [];

  questionGroups.forEach((questionGroup) => {
    if (!questionGroup.passageId) {
      groups.push({
        passageId: null,
        passageRaw: "",
        questions: [questionGroup],
      });

      return;
    }

    const existingGroup = groups.find(
      (group) =>
        String(group.passageId) ===
        String(questionGroup.passageId)
    );

    if (existingGroup) {
      existingGroup.questions.push(questionGroup);
      return;
    }

    groups.push({
      passageId: questionGroup.passageId,
      passageRaw: questionGroup.passageRaw,
      questions: [questionGroup],
    });
  });

  return groups;
}


function SelfGradingContent() {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const [marks, setMarks] = useState({});
  const [markErrors, setMarkErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [editingPublishedGrade, setEditingPublishedGrade] = useState(false);
  const [marksInitialized, setMarksInitialized] = useState(false);
  const [paperHeaderAssessment, setPaperHeaderAssessment] = useState(null);

  const exam = location.state?.exam || {
    candidate_id: Number(id),
    attempt_no: 1,
    assessment_id: Number(new URLSearchParams(location.search).get("assessment_id")),
  };

  const {
    report,
    loadReport,
    isGradePublished,
    loadGradingPaper,
    buildSelfGradingDataset,
    selfGradingQuestions,
    saveSelfGradingMarks,
    clearSelfGradingMarks,
    publishSelfGrade,
    unpublishSelfGrade,
    reportLoading,
  } = useExamGradingContext();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!exam?.candidate_id || !exam?.assessment_id) return;

      const reportData = await loadReport(exam);
      const gradingData = await loadGradingPaper(exam);
      const assessmentDetails = gradingData.assessment_details;

      const startTime = reportData.attempt_start_time;

      if (!cancelled) {
        setPaperHeaderAssessment({
          assessment_details: assessmentDetails,
          start_time: startTime,
        });

        buildSelfGradingDataset(gradingData);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exam?.candidate_id, exam?.assessment_id, exam?.attempt_no]);

  useEffect(() => {
    if (marksInitialized) return;

    const initialMarks = {};

    getSelfGradingRows(selfGradingQuestions).forEach((row) => {
      initialMarks[row.question_id] = row.markObtained ?? "";
    });

    if (Object.keys(initialMarks).length > 0) {
      setMarks(initialMarks);
      setMarksInitialized(true);
    }
  }, [selfGradingQuestions, marksInitialized]);

  const updateMark = (row, value) => {
    const numericValue = value === "" ? "" : Number(value);

    setMarks((prev) => ({
      ...prev,
      [row.question_id]: numericValue,
    }));

    if (value === "") {
      setMarkErrors((prev) => ({
        ...prev,
        [row.question_id]: "Mark is required.",
      }));
      return;
    }

    if (numericValue < 0 || numericValue > row.markAllocation) {
      setMarkErrors((prev) => ({
        ...prev,
        [row.question_id]: `Mark must be between 0 and ${row.markAllocation}.`,
      }));
      return;
    }

    setMarkErrors((prev) => {
      const next = { ...prev };
      delete next[row.question_id];
      return next;
    });
  };

  const getMissingMarkErrors = () => {
    const errors = {};

    getSelfGradingRows(selfGradingQuestions).forEach((row) => {
      if (row.isLocked) return;

      const value = marks[row.question_id];

      if (value === "" || value === null || value === undefined) {
        errors[row.question_id] = "Please enter marks for this question.";
      }
    });

    return errors;
  };

  const summary = useMemo(() => {
    const rows = getSelfGradingRows(selfGradingQuestions);

    const totalMarks = rows.reduce(
      (sum, row) => sum + Number(row.markAllocation || 0),
      0
    );

    const rowMarks = rows.reduce((sum, row) => {
      const value = marks[row.question_id];

      if (value === "" || value === null || value === undefined) {
        return sum;
      }

      return sum + Number(value || 0);
    }, 0);

    const reportMarks =
      report?.total_mark_obtained ??
      report?.final_grade ??
      0;

    const displayMarks = rowMarks || Number(reportMarks || 0);

    return {
      total: rows.length,
      currentMarks: displayMarks,
      totalMarks,
      percentage: totalMarks
        ? `${Math.round((displayMarks / totalMarks) * 100)}%`
        : "-",
    };
  }, [selfGradingQuestions, marks, report]);

  
  const isReadOnlyPublished = isGradePublished && !editingPublishedGrade;
  const hasMissingMarks = isReadOnlyPublished
    ? false
    : Object.keys(getMissingMarkErrors()).length > 0;

  const hasErrors = Object.keys(markErrors).length > 0 || hasMissingMarks;
  const handleSave = async () => {
    setSaving(true);

    try {
      await saveSelfGradingMarks({ exam, marks });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    const missingErrors = getMissingMarkErrors();

    if (Object.keys(missingErrors).length > 0) {
      setMarkErrors((prev) => ({
        ...prev,
        ...missingErrors,
      }));

      alert("Please enter marks for all questions before publishing.");
      return;
    }

    setSaving(true);
    await saveSelfGradingMarks({ exam, marks });
    await publishSelfGrade({ exam, marks });
    setSaving(false);

    history.push({
      pathname: routes.exm_grade_report.replace(":id", exam.candidate_id),
      search: `?assessment_id=${exam.assessment_id}&attempt_no=${exam.attempt_no || 1}`,
      state: { exam },
    });
  };



  const handleUnpublish = async () => {
    setSaving(true);

    try {
      await unpublishSelfGrade({ exam });

      setEditingPublishedGrade(false);
      setMarkErrors({});
    } finally {
      setSaving(false);
    }
  };


  const handleFileDownload = async (
    questionId,
    fileName,
    isModelAnswer = false
  ) => {
    try {
      const containerName = config.storageBlobConfigs.containerName;
      const cleanFileName = String(fileName)
        .replace(/['"]+/g, "")
        .trim();
      // OLD WORKING WAY for model answers
      const blobName = isModelAnswer
        ? cleanFileName
        : `${exam.candidate_id}-${questionId}-${cleanFileName}`;

      await AzureStorage.downloadBlob(containerName, blobName);
    } catch (error) {
      console.error("File download failed", error);
      window.Swal.fire(
        "Failed!",
        "File download failed. Please try again.",
        "error"
      );
    }
  };

  return (
    <ContentWrapper>
      <section className="self-grading-page">
        <div className="row">
          <div className="col-12">
            <div className="exam-paper-page__main self-grading-shell">
              <div className="exam-paper-page__content">
                <div className="exam-paper-page__title-row">
                  <button
                    type="button"
                    className="exam-paper-page__back-btn"
                    onClick={() =>
                      history.push({
                        pathname: routes.exm_grade_report.replace(
                          ":id",
                          exam.candidate_id
                        ),
                        search: `?assessment_id=${exam.assessment_id}&attempt_no=${exam.attempt_no || 1}`,
                        state: { exam },
                      })
                    }
                    aria-label="Back to grade report"
                    title="Back to grade report"
                  >
                    <HiArrowLeft aria-hidden="true" />
                  </button>

                  <div className="exam-paper-page__title">
                    {paperHeaderAssessment && (
                      <StudentPaperHeader
                        assessment={paperHeaderAssessment}
                        subtitle="Review candidate answers, model answers, guidelines, and enter marks for manual questions."
                        showStartTime={true}
                      />
                    )}
                  </div>
                </div>

                <div className="row g-0 self-grading-summary">
                  <div className="col-12 col-md-4">
                    <div className="self-grading-summary-card">
                      <span>Exam</span>
                      <strong>{exam.name || exam.title || "Self Grading"}</strong>
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="self-grading-summary-card">
                      <span>Questions Reviewed</span>
                      <strong>{summary.total}</strong>
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div className="self-grading-summary-card">
                      <span>Current Score View</span>
                      <strong>
                        {summary.currentMarks} / {summary.totalMarks} ({summary.percentage})
                      </strong>
                    </div>
                  </div>
                </div>
                <div className="self-grading-toolbar">
                  <button
                    type="button"
                    className="self-grading-btn self-grading-btn--clear"
                    disabled={saving || isReadOnlyPublished}
                    onClick={async () => {
                      setSaving(true);

                      await clearSelfGradingMarks({ exam });

                      const clearedMarks = {};

                      getSelfGradingRows(selfGradingQuestions).forEach((row) => {
                        clearedMarks[row.question_id] = row.isLocked ? row.markObtained : "";
                      });

                      setMarks(clearedMarks);
                      setMarkErrors({});
                      setSaving(false);
                    }}
                  >
                    {saving ? "Clearing..." : "Clear Grades"}
                  </button>

                  <div className="self-grading-toolbar__right">
                    {isReadOnlyPublished && (
                      <button
                        type="button"
                        className="self-grading-btn self-grading-btn--clear"
                        disabled={saving}
                        onClick={handleUnpublish}
                      >
                        {saving ? "Unpublishing..." : "Unpublish Grade"}
                      </button>
                    )}
                    <button
                      type="button"
                      className="self-grading-btn self-grading-btn--save"
                      disabled={saving || hasErrors || isReadOnlyPublished}
                      onClick={handleSave}
                    >
                      {saving ? "Saving..." : "Save Changes"}
                    </button>

                    <button
                      type="button"
                      className="self-grading-btn self-grading-btn--publish"
                      disabled={saving || hasErrors || isReadOnlyPublished}
                      onClick={handlePublish}
                    >
                      {saving ? "Publishing..." : "Publish Grade"}
                    </button>
                  </div>
                </div>

                <div className="exam-paper-page__questions">
                  {reportLoading ? (
                    <Spinner2 text="Loading" />
                  ) : selfGradingQuestions.length === 0 ? (
                    <p></p>
                  ) : (
                    selfGradingQuestions.map((partGroup) => (
                      <div className="exam-part-block" key={partGroup.partKey}>
                        {partGroup.partName && (
                          <div className="exam-part-header">
                            <h2>{partGroup.partName}</h2>
                            {partGroup.partInstruction && <p>{partGroup.partInstruction}</p>}
                          </div>
                        )}

                        {partGroup.sections.map((sectionGroup) => (
                          <div className="exam-section-block" key={sectionGroup.sectionKey}>
                            {sectionGroup.sectionName && (
                              <div className="exam-section-header">
                                <h3>{sectionGroup.sectionName}</h3>
                                {sectionGroup.sectionInstruction && (
                                  <p>{sectionGroup.sectionInstruction}</p>
                                )}
                              </div>
                            )}

                            {groupByPassage(sectionGroup.questionGroups).map(
                              (passageGroup, passageIndex) => (
                                <React.Fragment
                                  key={
                                    passageGroup.passageId
                                      ? `passage-${passageGroup.passageId}`
                                      : `question-${passageIndex}`
                                  }
                                >
                                  {passageGroup.passageId && passageGroup.passageRaw && (
                                    <div className="exam-question-card">
                                      <div className="self-grading-answer-block self-grading-answer-block--reference">
                                        <div className="self-grading-reference-copy">
                                          {renderDraftContent(passageGroup.passageRaw)}
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {passageGroup.questions.map((group) => (
                                    <article
                                      className="exam-question-card"
                                      key={group.parentQuestionId}
                                    >
                                      {group.parentRaw && group.children.length > 1 && (
                                        <div className="exam-question-card__header">
                                          <div>
                                            <strong>Question {group.parentIndex + 1}</strong>

                                            {renderDraftContent(
                                              group.parentRaw,
                                              "self-grading-question-content"
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {group.children.map((row, childIndex) => (
                                        <div
                                    className="self-grading-subquestion-card"
                                    key={row.question_id}
                                  >
                                    <div className="exam-question-card__header">
                                      <div>
                                        <strong>
                                          {group.children.length > 1
                                            ? `Subquestion ${childIndex + 1}`
                                            : `Question ${group.parentIndex + 1}`}
                                        </strong>

                                        {renderDraftContent(
                                          row.questionRaw,
                                          "self-grading-question-content"
                                        )}
                                      </div>

                                      {row.isLocked && (
                                        <span className="self-grading-status-pill">
                                          <HiDocumentDuplicate aria-hidden="true" />
                                          Auto-graded / locked
                                        </span>
                                      )}
                                    </div>

                                    {row.isMcq ? (
                                      <>
                                        <div>
                                          {(() => {
                                            const options = row.mcqOptions || [];
                                            const selectedAnswers = row.mcqSelectedIndexes || [];
                                            const correctIndexes = row.mcqCorrectIndexes || [];

                                            const sortedSelectedAnswers = [...selectedAnswers].sort((a, b) => a - b);
                                            const sortedCorrectIndexes = [...correctIndexes].sort((a, b) => a - b);

                                            const isCandidateCorrect =
                                              sortedSelectedAnswers.length === sortedCorrectIndexes.length &&
                                              sortedSelectedAnswers.every(
                                                (answerIndex, index) => answerIndex === sortedCorrectIndexes[index]
                                              );

                                            return (
                                              <>
                                                <div className="row g-3">
                                                  {options.map((option) => {
                                                    const optionIndex = option.index;
                                                    const isSelected = selectedAnswers.includes(optionIndex);
                                                    const isCorrectAnswer = correctIndexes.includes(optionIndex);
                                                    const isWrongSelected = isSelected && !isCorrectAnswer;

                                                    const optionClassName = [
                                                      "exam-option",
                                                      "result-sheet-option",
                                                      isCorrectAnswer ? "result-sheet-option--correct" : "",
                                                      isWrongSelected ? "result-sheet-option--wrong" : "",
                                                    ]
                                                      .filter(Boolean)
                                                      .join(" ");

                                                    return (
                                                      <div className="col-12 col-md-6" key={`${row.question_id}-${optionIndex}`}>
                                                        <div className={optionClassName}>
                                                          <span className="exam-option__indicator" />

                                                          <div className="result-sheet-option__copy">
                                                            <span className="exam-option__text">
                                                              {renderSingleDraftRaw(option.raw)}
                                                            </span>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    );
                                                  })}
                                                </div>

                                                <div
                                                  className={`result-sheet-mcq-result ${isCandidateCorrect
                                                      ? "result-sheet-mcq-result--correct"
                                                      : "result-sheet-mcq-result--wrong"
                                                    }`}
                                                >
                                                  <strong>{isCandidateCorrect ? "Correct answer" : "Wrong answer"}</strong>

                                                  <div className="result-sheet-mcq-result__answers">
                                                    <div>
                                                      <span>Candidate Answer</span>
                                                      <p>{row.candidateAnswerText || "No answer selected."}</p>
                                                    </div>

                                                    <div>
                                                      <span>Model Answer</span>
                                                      <p>{row.modelAnswerText || "No model answer available."}</p>
                                                    </div>
                                                  </div>
                                                </div>
                                              </>
                                            );
                                          })()}
                                        </div>

                                        <div className="self-grading-guidance-block">
                                          <div className="self-grading-guidance-header">
                                            <HiInformationCircle aria-hidden="true" />
                                            <span>Marking Guidance</span>
                                          </div>
                                          <div className="self-grading-guidance-copy">
                                            {renderDraftContent(row.guidelineRaw) || "No guidelines available."}
                                          </div>
                                        </div>
                                      </>
                                    ) : (
                                      <div className="self-grading-answer-grid">
                                        <div className="self-grading-answer-block">
                                          <span className="self-grading-answer-label">Candidate Answer</span>

                                          <div className="exam-descriptive-answer self-grading-answer-copy">
                                            {row.candidateAnswerText && <p>{row.candidateAnswerText}</p>}

                                            {row.candidateAnswerFiles?.length > 0 && (
                                              <div className="self-grading-uploaded-files">
                                                {row.candidateAnswerFiles.map((fileName) => (
                                                  <button
                                                    type="button"
                                                    key={fileName}
                                                    className="exam-paper-chip"
                                                    onClick={() => handleFileDownload(row.question_id, fileName)}
                                                  >
                                                    {fileName}
                                                  </button>
                                                ))}
                                              </div>
                                            )}

                                            {!row.candidateAnswerText && !row.candidateAnswerFiles?.length
                                              ? "No answer submitted."
                                              : null}
                                          </div>
                                        </div>

                                          <div className="self-grading-answer-block self-grading-answer-block--reference">
                                            <span className="self-grading-answer-label">Model Answer</span>
                                            {renderModelAnswer(row, handleFileDownload)}
                                          </div>

                                          <div className="self-grading-guidance-block">
                                            <div className="self-grading-guidance-header">
                                            <HiInformationCircle aria-hidden="true" />
                                            <span>Marking Guidance</span>
                                          </div>
                                          <div className="self-grading-guidance-copy">
                                            {renderDraftContent(row.guidelineRaw) || "No guidelines available."}
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    <div className="self-grading-answer-block">
                                      <span className="self-grading-answer-label">
                                        Marks / {row.markAllocation}
                                      </span>

                                      <input
                                        type="number"
                                        min="0"
                                        max={row.markAllocation}
                                        disabled={row.isLocked || isReadOnlyPublished}
                                        value={marks[row.question_id] ?? ""}
                                        onChange={(e) => updateMark(row, e.target.value)}
                                        onWheel={(e) => e.currentTarget.blur()}
                                        onKeyDown={(e) => {
                                          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                                            e.preventDefault();
                                          }
                                        }}
                                      />

                                      {markErrors[row.question_id] && (
                                        <small className="text-danger">
                                          {markErrors[row.question_id]}
                                        </small>
                                      )}

                                      {row.isLocked && (
                                        <small>
                                                MCQ mark is auto-graded and cannot be edited.
                                              </small>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </article>
                                  ))}
                                </React.Fragment>
                              )
                            )}
                          </div>
                        ))}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </ContentWrapper>
  );
}

export default function SelfGrading() {
  return (
    <ExamGradingProvider>
      <SelfGradingContent />
    </ExamGradingProvider>
  );
}
